// src/components/CardEditor/RichTextEditor.tsx
// ✅ CORRIGIDO:
//   1. Colar texto: limpa HTML externo, preserva formatação simples
//   2. Cores de texto e marcador: foco garantido antes de execCommand
//   3. Lista alfabética: preserva texto selecionado como primeiro item
//   4. Desfazer/Refazer: botões Ctrl+Z / Ctrl+Y nativos + botões na toolbar
//   (Upload Cloudinary mantido intacto)

import React, { useRef, useEffect, useState, useCallback } from 'react';
import {
    Bold,
    Italic,
    Underline,
    Superscript,
    Palette,
    Highlighter,
    List,
    ListOrdered,
    Image as ImageIcon,
    X,
    RotateCcw,
    AlignLeft,
    AlignCenter,
    AlignRight,
    AlignJustify,
    Music,
    Undo,
    Redo
} from 'lucide-react';
import './RichTextEditor.css';
import { ImageZoomModal } from '../CardEditor/ImageZoomModal';

interface RichTextEditorProps {
    content: string;
    setContent: (content: string) => void;
    placeholder?: string;
    editorType: 'front' | 'back' | 'support';
    editorId: string;
    subdeckName?: string;
    isRestricted?: boolean;
}

export const RichTextEditor: React.FC<RichTextEditorProps> = ({
    content,
    setContent,
    placeholder = 'Insira o conteúdo',
    editorType,
    editorId,
    subdeckName = 'geral',
    isRestricted = false
}) => {
    const editorRef = useRef<HTMLDivElement>(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [showHighlightPicker, setShowHighlightPicker] = useState(false);
    const [zoomImage, setZoomImage] = useState<string | null>(null);
    const [zoomScale, setZoomScale] = useState<number>(1);
    const [showAudioMenu, setShowAudioMenu] = useState(false);
    const [showSymbolMenu, setShowSymbolMenu] = useState(false);
    const multiSelectRangesRef = useRef<Range[]>([]);
    const pendingFirstSelectionRef = useRef<Range | null>(null);
    const undoStackRef = useRef<string[]>([]);
    const redoStackRef = useRef<string[]>([]);
    const isUndoRedoRef = useRef(false);
    const symbolBtnRef = useRef<HTMLButtonElement>(null);
    const symbolDropdownRef = useRef<HTMLDivElement>(null);

    const textColors = [
        '#000000', '#ffffff', '#dc2626', '#2563eb', '#059669', '#7c3aed',
        '#ea580c', '#be123c', '#0284c7', '#16a34a', '#db2777', '#4b5563'
    ];

    const highlightColors = [
        '#fef08a', '#bbf7d0', '#a5f3fc', '#f9a8d4', '#fed7aa', '#fecdd3',
        '#d9f99d', '#bfdbfe', '#fde68a', '#fbcfe8', '#d1fae5', '#e9d5ff'
    ];

    const symbols = [
        { char: '°', label: 'Grau' },
        { char: 'º', label: 'Ordinal masculino' },
        { char: 'ª', label: 'Ordinal feminino' },
        { char: '→', label: 'Seta direita' },
        { char: '←', label: 'Seta esquerda' },
        { char: '↑', label: 'Seta cima' },
        { char: '↓', label: 'Seta baixo' },
        { char: '≥', label: 'Maior ou igual' },
        { char: '≤', label: 'Menor ou igual' },
        { char: '≠', label: 'Diferente' },
        { char: '±', label: 'Mais ou menos' },
        { char: '×', label: 'Multiplicação' },
        { char: '÷', label: 'Divisão' },
        { char: '∑', label: 'Somatório' },
        { char: '√', label: 'Raiz quadrada' },
        { char: '∞', label: 'Infinito' },
        { char: 'α', label: 'Alfa' },
        { char: 'β', label: 'Beta' },
        { char: 'γ', label: 'Gama' },
        { char: 'δ', label: 'Delta' },
        { char: 'μ', label: 'Mi' },
        { char: 'Δ', label: 'Delta maiúsculo' },
    ];

    // =========================
    // SINCRONIZA CONTEÚDO
    // =========================
    useEffect(() => {
        if (!editorRef.current) return;

        const currentDOM = editorRef.current.innerHTML;

        // Montagem inicial: preenche com conteúdo existente
        if (currentDOM === '' && content !== '') {
            editorRef.current.innerHTML = content;
            undoStackRef.current = [content]; // ✅ Inicializa histórico com conteúdo existente
            return;
        }

        // ✅ Card novo inserido no meio: conteúdo vazio força limpeza do DOM
        if (content === '' && currentDOM !== '') {
            editorRef.current.innerHTML = '';
            undoStackRef.current = ['']; // ✅ Reseta histórico
            redoStackRef.current = [];
        }

        // Edição normal: DOM é a fonte da verdade, não sobrescreve
    }, [content]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        const handleOpenZoom = (e: any) => {
            setZoomImage(e.detail);
            setZoomScale(1);
        };
        window.addEventListener('openImageZoom', handleOpenZoom);
        return () => window.removeEventListener('openImageZoom', handleOpenZoom);
    }, []);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.audio-dropdown-container')) {
                setShowAudioMenu(false);
            }
        };
        if (showAudioMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showAudioMenu]);

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.symbol-dropdown-container')) {
                setShowSymbolMenu(false);
            }
        };
        if (showSymbolMenu) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showSymbolMenu]);

    useEffect(() => {
        if (showSymbolMenu && symbolBtnRef.current && symbolDropdownRef.current) {
            const rect = symbolBtnRef.current.getBoundingClientRect();
            symbolDropdownRef.current.style.top = `${rect.bottom + 4}px`;
            symbolDropdownRef.current.style.left = `${rect.left}px`;
        }
    }, [showSymbolMenu]);

    const handleInput = useCallback(() => {
        if (!editorRef.current) return;
        const html = editorRef.current.innerHTML;

        // ✅ Só empilha se não for uma operação de undo/redo
        if (!isUndoRedoRef.current) {
            undoStackRef.current.push(html);
            // Limpa o redo quando o usuário digita algo novo
            redoStackRef.current = [];
            // Limita o histórico a 100 entradas
            if (undoStackRef.current.length > 100) {
                undoStackRef.current.shift();
            }
        }

        setContent(html);
    }, [setContent]);

    // =========================
    // ✅ FIX 1: COLAR TEXTO LIMPO
    // Intercepta o paste, extrai texto puro preservando
    // apenas quebras de parágrafo reais (não word-wrap de sites)
    // =========================
    const handlePaste = useCallback((e: React.ClipboardEvent<HTMLDivElement>) => {
        e.preventDefault();

        const clipboardData = e.clipboardData;

        // Tenta pegar HTML do clipboard
        const htmlData = clipboardData.getData('text/html');
        const plainText = clipboardData.getData('text/plain');

        let cleanedText = '';

        if (htmlData) {
            // Parse o HTML e extrai texto de forma inteligente
            const parser = new DOMParser();
            const doc = parser.parseFromString(htmlData, 'text/html');

            // Remove elementos desnecessários
            const removeSelectors = [
                'script', 'style', 'head', 'meta', 'link',
                'nav', 'header', 'footer', 'aside', 'noscript'
            ];
            removeSelectors.forEach(sel => {
                doc.querySelectorAll(sel).forEach(el => el.remove());
            });

            // Converte o HTML para texto preservando estrutura semântica
            cleanedText = convertHtmlToCleanText(doc.body);
        } else {
            // Sem HTML: usa texto plano diretamente
            cleanedText = plainText;
        }

        // Remove quebras de linha excessivas (mais de 2 seguidas)
        cleanedText = cleanedText
            .replace(/\r\n/g, '\n')
            .replace(/\r/g, '\n')
            .replace(/\n{3,}/g, '\n\n')
            .trim();

        // Insere o texto limpo na posição do cursor
        const selection = window.getSelection();
        if (!selection || !selection.rangeCount) return;

        const range = selection.getRangeAt(0);
        range.deleteContents();

        // Converte para HTML simples (parágrafo por bloco)
        const lines = cleanedText.split('\n');
        const fragment = document.createDocumentFragment();

        lines.forEach((line, idx) => {
            if (line.trim() === '') {
                // Linha vazia = espaço entre parágrafos
                if (idx > 0 && idx < lines.length - 1) {
                    fragment.appendChild(document.createElement('br'));
                }
            } else {
                const span = document.createElement('span');
                span.textContent = line;
                fragment.appendChild(span);
                // Adiciona quebra entre linhas (não na última)
                if (idx < lines.length - 1 && lines[idx + 1].trim() !== '') {
                    fragment.appendChild(document.createElement('br'));
                } else if (idx < lines.length - 1) {
                    fragment.appendChild(document.createElement('br'));
                }
            }
        });

        range.insertNode(fragment);

        // Move cursor para o final do conteúdo inserido
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);

        handleInput();
    }, [handleInput]);

    // Helper: converte HTML para texto limpo preservando estrutura
    const convertHtmlToCleanText = (element: Element): string => {
        let result = '';

        const processNode = (node: Node): string => {
            if (node.nodeType === Node.TEXT_NODE) {
                // Texto: preserva mas normaliza espaços internos
                return (node.textContent || '').replace(/\s+/g, ' ');
            }

            if (node.nodeType !== Node.ELEMENT_NODE) return '';

            const el = node as Element;
            const tagName = el.tagName.toLowerCase();

            // Tags que devem ser ignoradas completamente
            if (['script', 'style', 'head'].includes(tagName)) return '';

            // Tags de formatação inline que preservamos
            const inlineTags = ['strong', 'b', 'em', 'i', 'u', 'span', 'a', 'sup', 'sub', 'mark'];
            if (inlineTags.includes(tagName)) {
                return Array.from(node.childNodes).map(processNode).join('');
            }

            // Tags de bloco: adiciona quebra de linha APÓS o bloco
            const blockTags = ['p', 'div', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
                'li', 'td', 'th', 'blockquote', 'pre', 'section', 'article'];

            if (blockTags.includes(tagName)) {
                const innerText = Array.from(node.childNodes).map(processNode).join('').trim();
                if (!innerText) return '';
                return innerText + '\n';
            }

            // BR: quebra de linha simples
            if (tagName === 'br') return '\n';

            // ul/ol: processa filhos normalmente
            if (tagName === 'ul' || tagName === 'ol') {
                return Array.from(node.childNodes).map(processNode).join('') + '\n';
            }

            // Outros: processa filhos
            return Array.from(node.childNodes).map(processNode).join('');
        };

        result = Array.from(element.childNodes).map(processNode).join('');

        // Limpa espaços antes de quebras de linha
        result = result.replace(/ +\n/g, '\n').replace(/\n +/g, '\n');

        return result;
    };

    useEffect(() => {
        if (!editorRef.current) return;

        const attachImageListeners = () => {
            const images = editorRef.current?.querySelectorAll('.flashcard-img-thumbnail');
            images?.forEach((img) => {
                const htmlImg = img as HTMLImageElement;
                const newImg = htmlImg.cloneNode(true) as HTMLImageElement;
                htmlImg.parentNode?.replaceChild(newImg, htmlImg);

                newImg.addEventListener('click', () => {
                    setZoomImage(newImg.src);
                });
            });

            const deleteButtons = editorRef.current?.querySelectorAll('.delete-img-btn-overlay');
            deleteButtons?.forEach((btn) => {
                const htmlBtn = btn as HTMLButtonElement;
                htmlBtn.style.cssText = `
                    position: absolute; top: -8px; right: -8px; width: 24px; height: 24px;
                    background: #dc2626; color: white; border: 2px solid white;
                    border-radius: 50%; cursor: pointer; font-size: 14px; font-weight: 700;
                    display: flex !important; align-items: center; justify-content: center;
                    transition: all 0.15s ease; box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                    z-index: 10; line-height: 1; padding: 0;
                `;
                const newBtn = htmlBtn.cloneNode(true) as HTMLButtonElement;
                htmlBtn.parentNode?.replaceChild(newBtn, htmlBtn);
                newBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    newBtn.closest('.card-image-wrapper')?.remove();
                    handleInput();
                });
            });
        };

        attachImageListeners();

        const attachAudioListeners = () => {
            const deleteButtons = editorRef.current?.querySelectorAll('.delete-audio-btn');
            deleteButtons?.forEach((btn) => {
                const htmlBtn = btn as HTMLButtonElement;
                htmlBtn.style.cssText = `
                    margin-top: 10px; background: rgba(220,38,38,0.9); color: white;
                    border: none; padding: 8px 16px; border-radius: 6px; cursor: pointer;
                    font-size: 13px; font-weight: 600; transition: all 0.15s ease; display: block;
                `;
                const newBtn = htmlBtn.cloneNode(true) as HTMLButtonElement;
                htmlBtn.parentNode?.replaceChild(newBtn, htmlBtn);
                newBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    newBtn.closest('.card-audio-wrapper')?.remove();
                    handleInput();
                });
            });
        };

        attachAudioListeners();
    }, [content, handleInput]);

    // =========================
    // ✅ FIX 2: FORMATAÇÃO COM FOCO GARANTIDO
    // Salva a seleção, foca o editor, restaura seleção, executa comando
    // =========================
    const savedSelectionRef = useRef<{ start: number; end: number } | null>(null);

    const saveSelection = () => {
        const selection = window.getSelection();
        if (!selection || !editorRef.current) return;
        if (selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const preSelectionRange = range.cloneRange();
        preSelectionRange.selectNodeContents(editorRef.current);
        preSelectionRange.setEnd(range.startContainer, range.startOffset);
        const start = preSelectionRange.toString().length;

        savedSelectionRef.current = {
            start,
            end: start + range.toString().length
        };
    };

    const restoreSelection = () => {
        if (!savedSelectionRef.current || !editorRef.current) return;

        const { start, end } = savedSelectionRef.current;
        const selection = window.getSelection();
        if (!selection) return;

        let charIndex = 0;
        const range = document.createRange();
        range.setStart(editorRef.current, 0);
        range.collapse(true);

        const nodeStack: Node[] = [editorRef.current];
        let node: Node | undefined;
        let foundStart = false;
        let stop = false;

        while (!stop && (node = nodeStack.pop())) {
            if (node.nodeType === Node.TEXT_NODE) {
                const nextCharIndex = charIndex + (node.textContent?.length || 0);

                if (!foundStart && start >= charIndex && start <= nextCharIndex) {
                    range.setStart(node, start - charIndex);
                    foundStart = true;
                }

                if (foundStart && end >= charIndex && end <= nextCharIndex) {
                    range.setEnd(node, end - charIndex);
                    stop = true;
                }

                charIndex = nextCharIndex;
            } else {
                const children = Array.from(node.childNodes).reverse();
                children.forEach(child => nodeStack.push(child));
            }
        }

        selection.removeAllRanges();
        selection.addRange(range);
    };

    const execCommandSafe = (command: string, value: string | null = null) => {
        if (!editorRef.current) return;

        // ✅ Se há seleções múltiplas salvas, aplica em todas
        if (multiSelectRangesRef.current.length > 0) {
            // Primeiro remove os highlights visuais e recaptura os ranges reais
            const highlights = editorRef.current.querySelectorAll('.multi-select-highlight');
            const rangesToFormat: Range[] = [];

            highlights.forEach(span => {
                const range = document.createRange();
                range.selectNodeContents(span);
                rangesToFormat.push(range);
            });

            // Aplica formatação em cada range
            const selection = window.getSelection();
            if (selection) {
                rangesToFormat.forEach(range => {
                    selection.removeAllRanges();
                    selection.addRange(range);
                    document.execCommand(command, false, value ?? undefined);
                });
                selection.removeAllRanges();
            }

            // Limpa highlights e estado
            clearMultiSelectHighlights();
            multiSelectRangesRef.current = [];
            handleInput();
            return;
        }

        // Comportamento normal (seleção única)
        saveSelection();
        editorRef.current.focus();
        restoreSelection();
        document.execCommand(command, false, value ?? undefined);
        handleInput();
    };

    // =========================
    // ✅ FIX 2: CORES COM FOCO GARANTIDO
    // =========================
    const applyTextColor = (color: string) => {
        if (!editorRef.current) return;
        saveSelection();
        editorRef.current.focus();
        restoreSelection();
        if (color === 'reset') {
            document.execCommand('removeFormat');
        } else {
            document.execCommand('foreColor', false, color);
        }
        handleInput();
        setShowColorPicker(false);
    };

    const applyHighlight = (color: string) => {
        if (!editorRef.current) return;
        saveSelection();
        editorRef.current.focus();
        restoreSelection();
        document.execCommand('backColor', false, color);
        handleInput();
        setShowHighlightPicker(false);
    };

    // =========================
    // ✅ FIX 3: LISTA ALFABÉTICA PRESERVANDO TEXTO SELECIONADO
    // =========================
    // Helper compartilhado: desmonta qualquer lista (ol/ul) em spans separados por <br>
    const unwrapList = (listEl: HTMLElement): void => {
        const items = Array.from(listEl.querySelectorAll('li'));
        const fragment = document.createDocumentFragment();
        items.forEach((li, idx) => {
            const span = document.createElement('span');
            span.textContent = li.textContent || '';
            fragment.appendChild(span);
            if (idx < items.length - 1) {
                fragment.appendChild(document.createElement('br'));
            }
        });
        listEl.replaceWith(fragment);
        handleInput();
    };

    const insertLetterList = () => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);
        const selectedText = range.toString();

        // ── TOGGLE OFF: já dentro de letter-list → remove ──
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'OL' &&
                (node as HTMLElement).classList.contains('letter-list')) {
                unwrapList(node as HTMLElement);
                return;
            }
            node = node.parentNode;
        }

        // Cria a lista
        const ol = document.createElement('ol');
        ol.className = 'letter-list';

        if (selectedText) {
            // Clona o fragmento DOM para extrair linhas reais (respeitando Enter/br/div)
            const fragmentClone = range.cloneContents();
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(fragmentClone);

            // Substitui <br> e elementos de bloco por marcador \n
            tempDiv.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
            tempDiv.querySelectorAll('div, p, li').forEach(block => {
                block.insertAdjacentText('afterend', '\n');
            });

            const rawText = tempDiv.textContent || '';
            const lines = rawText.split('\n').filter(l => l.trim() !== '');

            if (lines.length > 0) {
                lines.forEach(line => {
                    const li = document.createElement('li');
                    li.textContent = line.trim();
                    ol.appendChild(li);
                });
            } else {
                const li = document.createElement('li');
                li.textContent = selectedText.trim();
                ol.appendChild(li);
            }

            range.deleteContents();
            range.insertNode(ol);
        } else {
            // Sem seleção: cria item vazio
            const li = document.createElement('li');
            li.innerHTML = '&nbsp;';
            ol.appendChild(li);
            range.insertNode(ol);

            // Posiciona cursor dentro do item
            const newRange = document.createRange();
            newRange.selectNodeContents(li);
            newRange.collapse(false);
            selection.removeAllRanges();
            selection.addRange(newRange);
        }

        // Move cursor após a lista
        const newRange = document.createRange();
        newRange.setStartAfter(ol);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleInput();
    };

    // ✅ NOVO: Lista numerada apenas no texto selecionado
    const insertNumberedList = () => {
        if (!editorRef.current) return;

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const range = selection.getRangeAt(0);

        // ── TOGGLE: verifica se já está dentro de uma OL ──
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'OL') {
                // Já é uma lista numerada → remove, extrai texto puro
                unwrapList(node as HTMLElement);
                return;
            }
            node = node.parentNode;
        }

        // ── SWITCH: está dentro de UL → troca por OL ──
        node = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'UL') {
                const ul = node as HTMLElement;
                const items = Array.from(ul.querySelectorAll('li'));
                const newOl = document.createElement('ol');
                items.forEach(li => {
                    const newLi = document.createElement('li');
                    newLi.textContent = li.textContent || '';
                    newOl.appendChild(newLi);
                });
                ul.replaceWith(newOl);
                const newRange = document.createRange();
                newRange.setStartAfter(newOl);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                handleInput();
                return;
            }
            node = node.parentNode;
        }

        // ── INSERIR: sem lista → cria OL novo ──
        const ol = document.createElement('ol');

        if (!range.collapsed) {
            const fragmentClone = range.cloneContents();
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(fragmentClone);

            tempDiv.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
            tempDiv.querySelectorAll('div, p, li').forEach(block => {
                block.insertAdjacentText('afterend', '\n');
            });

            const rawText = tempDiv.textContent || '';
            const lines = rawText
                .split('\n')
                .map(l => l.trim())
                .filter(l => l !== '');

            if (lines.length > 0) {
                lines.forEach(line => {
                    const li = document.createElement('li');
                    li.textContent = line;
                    ol.appendChild(li);
                });
            } else {
                const li = document.createElement('li');
                li.textContent = rawText.trim();
                ol.appendChild(li);
            }

            range.deleteContents();
            range.insertNode(ol);
        } else {
            const li = document.createElement('li');
            li.innerHTML = '&nbsp;';
            ol.appendChild(li);
            range.insertNode(ol);
        }

        const newRange = document.createRange();
        newRange.setStartAfter(ol);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleInput();
    };

    const insertBulletList = () => {
        if (!editorRef.current) return;
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            execCommandSafe('insertUnorderedList');
            return;
        }

        const range = selection.getRangeAt(0);

        // ── TOGGLE OFF: já dentro de UL → remove ──
        let node: Node | null = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'UL') {
                unwrapList(node as HTMLElement);
                return;
            }
            node = node.parentNode;
        }

        // ── SWITCH: está dentro de OL → troca por UL ──
        node = range.commonAncestorContainer;
        while (node && node !== editorRef.current) {
            if ((node as HTMLElement).tagName === 'OL') {
                const ol = node as HTMLElement;
                const items = Array.from(ol.querySelectorAll('li'));
                const ul = document.createElement('ul');
                items.forEach(li => {
                    const newLi = document.createElement('li');
                    newLi.textContent = li.textContent || '';
                    ul.appendChild(newLi);
                });
                ol.replaceWith(ul);
                const newRange = document.createRange();
                newRange.setStartAfter(ul);
                newRange.collapse(true);
                selection.removeAllRanges();
                selection.addRange(newRange);
                handleInput();
                return;
            }
            node = node.parentNode;
        }

        // ── INSERIR: sem lista → cria UL novo (mesmo padrão do insertNumberedList) ──
        const ul = document.createElement('ul');

        if (!range.collapsed) {
            const fragmentClone = range.cloneContents();
            const tempDiv = document.createElement('div');
            tempDiv.appendChild(fragmentClone);

            tempDiv.querySelectorAll('br').forEach(br => br.replaceWith('\n'));
            tempDiv.querySelectorAll('div, p, li').forEach(block => {
                block.insertAdjacentText('afterend', '\n');
            });

            const rawText = tempDiv.textContent || '';
            const lines = rawText
                .split('\n')
                .map(l => l.trim())
                .filter(l => l !== '');

            if (lines.length > 0) {
                lines.forEach(line => {
                    const li = document.createElement('li');
                    li.textContent = line;
                    ul.appendChild(li);
                });
            } else {
                const li = document.createElement('li');
                li.textContent = rawText.trim();
                ul.appendChild(li);
            }

            range.deleteContents();
            range.insertNode(ul);
        } else {
            const li = document.createElement('li');
            li.innerHTML = '&nbsp;';
            ul.appendChild(li);
            range.insertNode(ul);
        }

        const newRange = document.createRange();
        newRange.setStartAfter(ul);
        newRange.collapse(true);
        selection.removeAllRanges();
        selection.addRange(newRange);

        handleInput();
    };

    // =========================
    // 🖼️ UPLOAD DE IMAGEM (CLOUDINARY)
    // =========================
    const uploadImageFile = async (file: File) => {
        if (!editorRef.current) return;

        const formData = new FormData();
        formData.append('image', file);
        formData.append('subdeckName', subdeckName);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/upload/card-image-cloudinary', {
                method: 'POST',
                body: formData,
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await response.json();
            if (!response.ok || !data.url) {
                alert(data.message || 'Erro ao enviar imagem');
                return;
            }

            const wrapper = document.createElement('div');
            wrapper.className = 'card-image-wrapper';
            wrapper.dataset.side = editorType;

            const divider = document.createElement('hr');
            divider.className = 'image-divider';

            const container = document.createElement('div');
            container.className = 'card-image-container';

            const thumbnailWrapper = document.createElement('div');
            thumbnailWrapper.className = 'image-thumbnail-wrapper';

            const img = document.createElement('img');
            img.src = data.url;
            img.className = 'flashcard-img-thumbnail';
            img.addEventListener('click', () => { setZoomImage(data.url); });

            const deleteBtn = document.createElement('button');
            deleteBtn.type = 'button';
            deleteBtn.className = 'delete-img-btn-overlay editor-only';
            deleteBtn.innerHTML = '✕';
            deleteBtn.title = 'Remover imagem';
            deleteBtn.style.cssText = `
                position: absolute; top: -8px; right: -8px; width: 24px; height: 24px;
                background: #dc2626; color: white; border: 2px solid white;
                border-radius: 50%; cursor: pointer; font-size: 14px; font-weight: 700;
                display: flex !important; align-items: center; justify-content: center;
                transition: all 0.15s ease; box-shadow: 0 2px 6px rgba(0,0,0,0.2);
                z-index: 10; line-height: 1; padding: 0;
            `;
            deleteBtn.addEventListener('click', (e) => {
                e.stopPropagation();
                wrapper.remove();
                handleInput();
            });

            thumbnailWrapper.appendChild(img);
            thumbnailWrapper.appendChild(deleteBtn);
            container.appendChild(thumbnailWrapper);
            wrapper.appendChild(divider);
            wrapper.appendChild(container);
            editorRef.current.appendChild(wrapper);
            handleInput();
            editorRef.current.focus();

        } catch (err) {
            console.error('Erro no upload:', err);
        }
    };

    const handleImageUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;
        await uploadImageFile(file);
        event.target.value = '';
    };

    const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
        if (e.dataTransfer.types.includes('Files')) {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'copy';
        }
    };

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        const imageFile = Array.from(e.dataTransfer.files).find(f => f.type.startsWith('image/'));
        if (!imageFile) return;
        e.preventDefault();
        await uploadImageFile(imageFile);
    };

    const insertSymbol = (char: string) => {
        if (!editorRef.current) return;

        editorRef.current.focus();

        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) {
            // Sem cursor: insere no final
            editorRef.current.innerHTML += char;
        } else {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            const textNode = document.createTextNode(char);
            range.insertNode(textNode);
            range.setStartAfter(textNode);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        handleInput();
        setShowSymbolMenu(false);
    };

    // =========================
    // 🎵 UPLOAD DE ÁUDIO (CLOUDINARY) — mantido intacto
    // =========================
    const handleAudioUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file || !editorRef.current) return;

        const allowedTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/x-wav'];
        if (!allowedTypes.includes(file.type)) {
            alert('Formato não suportado. Use MP3 ou WAV.');
            return;
        }
        if (file.size > 20 * 1024 * 1024) {
            alert('Arquivo muito grande. Máximo: 20MB');
            return;
        }

        const formData = new FormData();
        formData.append('audio', file);
        formData.append('subdeckName', subdeckName);

        try {
            const token = localStorage.getItem('accessToken');
            const response = await fetch('/api/upload/card-audio-cloudinary', {
                method: 'POST',
                body: formData,
                headers: { Authorization: `Bearer ${token}` }
            });

            const data = await response.json();
            if (!response.ok || !data.url) {
                alert(data.message || 'Erro ao enviar áudio');
                return;
            }

            const wrapper = document.createElement('div');
            wrapper.className = 'card-audio-wrapper editor-audio-wrapper';
            wrapper.dataset.side = editorType;
            wrapper.dataset.audioUrl = data.url;

            const divider = document.createElement('hr');
            divider.className = 'audio-divider';

            const container = document.createElement('div');
            container.className = 'card-audio-container';

            const audioPlayer = document.createElement('audio');
            audioPlayer.controls = true;
            audioPlayer.src = data.url;
            audioPlayer.style.cssText = `width: 100%; max-width: 500px; height: 40px;`;

            container.appendChild(audioPlayer);
            wrapper.appendChild(divider);
            wrapper.appendChild(container);
            editorRef.current.appendChild(wrapper);
            handleInput();
            editorRef.current.focus();
            setShowAudioMenu(false);

        } catch (err) {
            console.error('Erro no upload de áudio:', err);
            alert('Erro ao fazer upload do áudio');
        } finally {
            event.target.value = '';
        }
    };

    const hasAudio = () => {
        if (!editorRef.current) return false;
        return editorRef.current.querySelector('.card-audio-wrapper') !== null;
    };

    const handleRemoveAudio = () => {
        if (!editorRef.current) return;
        const audioWrapper = editorRef.current.querySelector('.card-audio-wrapper');
        if (audioWrapper) {
            audioWrapper.remove();
            handleInput();
        }
        setShowAudioMenu(false);
    };

    const handleMouseUp = useCallback((e: React.MouseEvent) => {
        if (!e.ctrlKey && !e.metaKey) {
            // Sem Ctrl: limpa seleções múltiplas salvas
            clearMultiSelectHighlights();
            multiSelectRangesRef.current = [];
            pendingFirstSelectionRef.current = null;
            return;
        }

        // Com Ctrl pressionado:
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;

        const currentRange = selection.getRangeAt(0);
        if (currentRange.collapsed) return;

        // ✅ Se existe uma primeira seleção pendente (feita sem Ctrl), salva ela primeiro
        if (pendingFirstSelectionRef.current) {
            highlightRange(pendingFirstSelectionRef.current);
            multiSelectRangesRef.current.push(pendingFirstSelectionRef.current);
            pendingFirstSelectionRef.current = null;
        }

        // Salva e destaca a seleção atual
        multiSelectRangesRef.current.push(currentRange.cloneRange());
        highlightRange(currentRange);

        selection.removeAllRanges();
    }, []);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        if (e.ctrlKey || e.metaKey) return; // Ctrl já está sendo tratado no mouseup

        // Salva a seleção atual ANTES de ela ser limpa pelo novo clique
        const selection = window.getSelection();
        if (selection && selection.rangeCount > 0 && !selection.getRangeAt(0).collapsed) {
            pendingFirstSelectionRef.current = selection.getRangeAt(0).cloneRange();
        } else {
            pendingFirstSelectionRef.current = null;
        }
    }, []);

    const highlightRange = (range: Range) => {
        try {
            const span = document.createElement('span');
            span.className = 'multi-select-highlight';
            range.surroundContents(span);
        } catch {
            // surroundContents falha se o range cruza elementos — usa extractContents
            const fragment = range.extractContents();
            const span = document.createElement('span');
            span.className = 'multi-select-highlight';
            span.appendChild(fragment);
            range.insertNode(span);
        }
    };

    const clearMultiSelectHighlights = () => {
        if (!editorRef.current) return;
        const highlights = editorRef.current.querySelectorAll('.multi-select-highlight');
        highlights.forEach(span => {
            const parent = span.parentNode;
            if (!parent) return;
            while (span.firstChild) {
                parent.insertBefore(span.firstChild, span);
            }
            parent.removeChild(span);
        });
        // Normaliza nós de texto fragmentados
        editorRef.current.normalize();
    };

    // =========================
    // ENTER EM LISTA ALFABÉTICA
    // =========================
    const handleKeyDown = (e: React.KeyboardEvent) => {
        // ✅ Atalhos de teclado
        if (e.ctrlKey || e.metaKey) {
            switch (e.key.toLowerCase()) {
                case 'z':
                    e.preventDefault();
                    handleUndo();
                    return;
                case 'y':
                    e.preventDefault();
                    handleRedo();
                    return;
                case 'n':
                    e.preventDefault();
                    execCommandSafe('bold');
                    return;
                case 'i':
                    // Ctrl+I já funciona nativamente, mas garantimos aqui
                    e.preventDefault();
                    execCommandSafe('italic');
                    return;
                case 'u':
                    // Ctrl+U já funciona nativamente
                    e.preventDefault();
                    execCommandSafe('underline');
                    return;
            }
        }

        if (e.key !== 'Enter') return;

        const sel = window.getSelection();
        if (!sel || !sel.rangeCount) return;

        let node = sel.getRangeAt(0).commonAncestorContainer as HTMLElement;

        while (node && node !== editorRef.current) {
            if (node.tagName === 'LI' && node.parentElement?.classList.contains('letter-list')) {
                e.preventDefault();

                const li = document.createElement('li');
                li.innerHTML = '&nbsp;';
                node.parentElement.insertBefore(li, node.nextSibling);

                const range = document.createRange();
                range.selectNodeContents(li);
                range.collapse(false);
                sel.removeAllRanges();
                sel.addRange(range);

                handleInput();
                return;
            }
            node = node.parentElement as HTMLElement;
        }
    };

    const insertPredefinedText = (text: string) => {
        if (!editorRef.current) return;

        editorRef.current.focus();

        const selection = window.getSelection();
        const strong = document.createElement('strong');
        strong.textContent = text;

        if (!selection || selection.rangeCount === 0) {
            editorRef.current.appendChild(strong);
        } else {
            const range = selection.getRangeAt(0);
            range.deleteContents();
            range.insertNode(strong);
            range.setStartAfter(strong);
            range.collapse(true);
            selection.removeAllRanges();
            selection.addRange(range);
        }

        handleInput();
    };

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            const target = e.target as HTMLElement;
            if (!target.closest('.color-picker-container')) {
                setShowColorPicker(false);
                setShowHighlightPicker(false);
            }
        };
        if (showColorPicker || showHighlightPicker) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showColorPicker, showHighlightPicker]);

    // =========================
    // ✅ FIX 4: DESFAZER / REFAZER
    // Usa o histórico nativo do browser via execCommand
    // =========================
    const handleUndo = () => {
        if (!editorRef.current || undoStackRef.current.length <= 1) return;

        // Remove o estado atual do undo e coloca no redo
        const currentState = undoStackRef.current.pop()!;
        redoStackRef.current.push(currentState);

        // Aplica o estado anterior
        const previousState = undoStackRef.current[undoStackRef.current.length - 1] ?? '';

        isUndoRedoRef.current = true;
        editorRef.current.innerHTML = previousState;
        setContent(previousState);
        isUndoRedoRef.current = false;
    };

    const handleRedo = () => {
        if (!editorRef.current || redoStackRef.current.length === 0) return;

        const nextState = redoStackRef.current.pop()!;
        undoStackRef.current.push(nextState);

        isUndoRedoRef.current = true;
        editorRef.current.innerHTML = nextState;
        setContent(nextState);
        isUndoRedoRef.current = false;
    };

    // =========================
    // RENDER
    // =========================
    return (
        <div className="rich-text-editor-container">
            <div className="rich-editor-toolbar">

                <button type="button" onClick={() => execCommandSafe('bold')} className="toolbar-btn" title="Negrito">
                    <Bold size={16} />
                </button>
                <button type="button" onClick={() => execCommandSafe('italic')} className="toolbar-btn" title="Itálico">
                    <Italic size={16} />
                </button>
                <button type="button" onClick={() => execCommandSafe('underline')} className="toolbar-btn" title="Sublinhado">
                    <Underline size={16} />
                </button>
                <button
                    type="button"
                    onClick={() => {
                        execCommandSafe('bold');
                        execCommandSafe('underline');
                    }}
                    className="toolbar-btn toolbar-btn-text"
                    title="Negrito e Sublinhado"
                    style={{ fontSize: '13px', fontWeight: 800, textDecoration: 'underline', minWidth: '30px' }}
                >
                    N
                </button>
                <button type="button" onClick={() => execCommandSafe('superscript')} className="toolbar-btn" title="Sobrescrito">
                    <Superscript size={16} />
                </button>

                <div className="toolbar-divider" />

                {/* Cor do texto */}
                <div className="color-picker-container">
                    <button
                        type="button"
                        className="toolbar-btn"
                        onMouseDown={(e) => {
                            // onMouseDown ao invés de onClick para salvar seleção ANTES de perder foco
                            e.preventDefault();
                            saveSelection();
                            setShowColorPicker(!showColorPicker);
                            setShowHighlightPicker(false);
                        }}
                        title="Cor do texto"
                    >
                        <Palette size={16} />
                    </button>
                    {showColorPicker && (
                        <div className="color-dropdown">
                            {textColors.map(c => (
                                <div
                                    key={c}
                                    className="color-option"
                                    style={{ backgroundColor: c }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyTextColor(c);
                                    }}
                                    title={c}
                                />
                            ))}
                            <button
                                type="button"
                                className="remove-highlight-btn"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    applyTextColor('reset');
                                }}
                            >
                                <RotateCcw size={14} />
                                Resetar
                            </button>
                        </div>
                    )}
                </div>

                {/* Marcador de texto */}
                <div className="color-picker-container">
                    <button
                        type="button"
                        className="toolbar-btn"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            saveSelection();
                            setShowHighlightPicker(!showHighlightPicker);
                            setShowColorPicker(false);
                        }}
                        title="Grifar texto"
                    >
                        <Highlighter size={16} />
                    </button>
                    {showHighlightPicker && (
                        <div className="color-dropdown">
                            {highlightColors.map(c => (
                                <div
                                    key={c}
                                    className="color-option"
                                    style={{ backgroundColor: c }}
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        applyHighlight(c);
                                    }}
                                    title={c}
                                />
                            ))}
                            <button
                                type="button"
                                className="remove-highlight-btn"
                                onMouseDown={(e) => {
                                    e.preventDefault();
                                    applyHighlight('transparent');
                                    setShowHighlightPicker(false);
                                }}
                            >
                                <X size={14} />
                                Remover grifo
                            </button>
                        </div>
                    )}
                </div>

                <div className="toolbar-divider" />

                {/* ✅ NOVO: Desfazer / Refazer */}
                <button type="button" onClick={handleUndo} className="toolbar-btn" title="Desfazer (Ctrl+Z)">
                    <Undo size={16} />
                </button>
                <button type="button" onClick={handleRedo} className="toolbar-btn" title="Refazer (Ctrl+Y)">
                    <Redo size={16} />
                </button>

                <div className="toolbar-divider" />

                <button
                    type="button"
                    onClick={insertNumberedList}
                    className="toolbar-btn"
                    title="Lista numerada"
                >
                    <ListOrdered size={16} />
                </button>
                <button
                    type="button"
                    onClick={insertBulletList}
                    className="toolbar-btn"
                    title="Lista com marcadores"
                >
                    <List size={16} />
                </button>
                <button
                    type="button"
                    onClick={insertLetterList}
                    className="toolbar-btn toolbar-btn-text"
                    title="Lista alfabética"
                >
                    a)
                </button>

                <div className="toolbar-divider" />

                <button type="button" onClick={() => execCommandSafe('justifyLeft')} className="toolbar-btn" title="Alinhar à esquerda">
                    <AlignLeft size={16} />
                </button>
                <button type="button" onClick={() => execCommandSafe('justifyCenter')} className="toolbar-btn" title="Centralizar">
                    <AlignCenter size={16} />
                </button>
                <button type="button" onClick={() => execCommandSafe('justifyRight')} className="toolbar-btn" title="Alinhar à direita">
                    <AlignRight size={16} />
                </button>
                <button type="button" onClick={() => execCommandSafe('justifyFull')} className="toolbar-btn" title="Justificar">
                    <AlignJustify size={16} />
                </button>

                <div className="toolbar-divider" />

                {!isRestricted && (
                    <>
                        <button
                            type="button"
                            className="toolbar-btn toolbar-btn-text"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                saveSelection();
                            }}
                            onClick={() => insertPredefinedText('Verdadeiro ou Falso:')}
                            title="Inserir Verdadeiro ou Falso"
                            style={{ fontSize: '11px', minWidth: '44px' }}
                        >
                            V/F
                        </button>

                        <button
                            type="button"
                            className="toolbar-btn toolbar-btn-text"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                saveSelection();
                            }}
                            onClick={() => insertPredefinedText('Complete os espaços:')}
                            title="Inserir Complete os espaços"
                            style={{ fontSize: '11px', minWidth: '44px' }}
                        >
                            C/E
                        </button>

                        <button
                            type="button"
                            className="toolbar-btn toolbar-btn-text"
                            onMouseDown={(e) => {
                                e.preventDefault();
                                saveSelection();
                            }}
                            onClick={() => insertPredefinedText('Ordem correta: → → →')}
                            title="Inserir Ordem correta"
                            style={{ fontSize: '11px', minWidth: '44px' }}
                        >
                            O/C
                        </button>

                        <div className="toolbar-divider" />
                    </>
                )}

                <div className="symbol-dropdown-container">
                    <button
                        ref={symbolBtnRef}
                        type="button"
                        className="toolbar-btn toolbar-btn-text"
                        onMouseDown={(e) => {
                            e.preventDefault();
                            saveSelection();
                            setShowSymbolMenu(!showSymbolMenu);
                        }}
                        title="Inserir símbolo"
                    >
                        Ω
                    </button>
                    {showSymbolMenu && (
                        <div ref={symbolDropdownRef} className="symbol-dropdown">
                            {symbols.map(s => (
                                <button
                                    key={s.char}
                                    type="button"
                                    className="symbol-option"
                                    onMouseDown={(e) => {
                                        e.preventDefault();
                                        insertSymbol(s.char);
                                    }}
                                    title={s.label}
                                >
                                    {s.char}
                                </button>
                            ))}
                        </div>
                    )}
                </div>

                <div className="toolbar-divider" />

                <label className="toolbar-btn" title="Adicionar imagem">
                    <ImageIcon size={16} />
                    <input type="file" accept="image/*" onChange={handleImageUpload} style={{ display: 'none' }} />
                </label>

                {!isRestricted && (
                    <div className="audio-dropdown-container">
                        <button
                            type="button"
                            className="toolbar-btn"
                            onClick={() => setShowAudioMenu(!showAudioMenu)}
                            title="Gerenciar áudio"
                        >
                            <Music size={16} />
                        </button>
                        {showAudioMenu && (
                            <div className="audio-dropdown-menu">
                                <label className="audio-dropdown-option">
                                    <span>Inserir áudio</span>
                                    <input
                                        type="file"
                                        accept="audio/mpeg,audio/mp3,audio/wav"
                                        onChange={handleAudioUpload}
                                        style={{ display: 'none' }}
                                    />
                                </label>
                                <button
                                    type="button"
                                    className="audio-dropdown-option audio-remove-option"
                                    onClick={handleRemoveAudio}
                                    disabled={!hasAudio()}
                                >
                                    <span>Remover áudio</span>
                                </button>
                            </div>
                        )}
                    </div>
                )}

            </div>

            <div
                ref={editorRef}
                contentEditable
                onInput={handleInput}
                onKeyDown={handleKeyDown}
                onPaste={handlePaste}
                onMouseDown={handleMouseDown}
                onMouseUp={handleMouseUp}
                onDragOver={handleDragOver}
                onDrop={handleDrop}
                data-placeholder={placeholder}
                className="rich-editor-content"
            />

            <ImageZoomModal
                imageUrl={zoomImage}
                onClose={() => {
                    setZoomImage(null);
                    setZoomScale(1);
                }}
                zoomScale={zoomScale}
                onZoomChange={setZoomScale}
            />
        </div>
    );
};